<template>
  <v-container fluid class="pa-0">
    <v-card>
      <v-card-title>
        <v-icon left>mdi-cash-multiple</v-icon>
        {{ $t('sites.sales_invoices.title') }}
        <v-spacer />
        <v-btn color="primary" :loading="syncing" @click="refresh">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('sites.sales_invoices.refresh') }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="!hasProject" type="warning" text dense class="mb-0">
          {{ $t('sites.sales_invoices.no_project') }}
        </v-alert>

        <v-data-table
          v-else
          :headers="headers"
          :items="invoices"
          :loading="loading"
          :items-per-page="25"
          sort-by="invoiceDate"
          sort-desc
          class="elevation-1"
          :no-data-text="$t('sites.sales_invoices.no_invoices')"
        >
          <template v-slot:body="{ items }">
            <tbody v-if="!items.length && !loading">
              <tr>
                <td :colspan="headers.length" class="text-center grey--text py-4">
                  {{ $t('sites.sales_invoices.no_invoices') }}
                </td>
              </tr>
            </tbody>
            <!-- Yksi <tbody> per lasku: perustiedot + riviselite ovat sama hoverattava ryhmä -->
            <tbody v-for="inv in items" :key="inv.netvisorKey" class="invoice-group">
              <tr>
                <td>{{ inv.customerName }}</td>
                <td>{{ inv.invoiceNumber }}</td>
                <td>{{ formatDate(inv.invoiceDate) }}</td>
                <td class="text-end">{{ formatCurrency(inv.siteAllocatedAmountExcludingVat) }}</td>
                <td class="text-end">{{ formatCurrency(inv.siteAllocatedAmount) }}</td>
                <td class="text-end">{{ formatCurrency(inv.totalAmount) }}</td>
                <td>{{ inv.invoiceStatus }}</td>
                <td class="text-end">
                  <v-btn
                    v-if="inv.netvisorUrl"
                    icon
                    :href="inv.netvisorUrl"
                    target="_blank"
                    :title="$t('sites.sales_invoices.open_in_netvisor')"
                  >
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-btn>
                </td>
              </tr>
              <!-- Työmaalle kohdistetut laskurivit: selite + rivin summat veroton/verollinen -->
              <tr v-for="(line, li) in inv.lines" :key="`${inv.netvisorKey}-${line.id || li}`"
                  class="line-row grey--text text--darken-2 caption">
                <td :colspan="3" class="line-title-cell">
                  {{ lineText(line) }}
                  <span v-if="lineQuantity(line)" class="grey--text">{{ lineQuantity(line) }}</span>
                  <span v-if="lineProductInfo(line)" class="grey--text">({{ lineProductInfo(line) }})</span>
                </td>
                <td class="text-end">{{ formatCurrency(line.lineSumExcludingVat) }}</td>
                <td class="text-end">{{ formatCurrency(line.lineSum) }}</td>
                <td :colspan="3"></td>
              </tr>
            </tbody>
            <tbody v-if="items.length">
              <tr class="total-row">
                <td :colspan="3" class="text-right font-weight-bold">
                  {{ $t('sites.sales_invoices.total_allocated') }}
                </td>
                <td class="text-end font-weight-bold">{{ formatCurrency(totalAllocatedExcludingVat) }}</td>
                <td class="text-end font-weight-bold">{{ formatCurrency(totalAllocated) }}</td>
                <td colspan="3"></td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import netvisorApi from '@/api/netvisor';

export default {
  name: 'SiteNetvisorSalesInvoices',
  props: {
    site: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      invoices: [],
      loading: false,
      syncing: false,
      headers: [
        { text: this.$t('sites.sales_invoices.customer'), value: 'customerName' },
        { text: this.$t('sites.sales_invoices.invoice_number'), value: 'invoiceNumber' },
        { text: this.$t('sites.sales_invoices.date'), value: 'invoiceDate' },
        {
          text: this.$t('sites.sales_invoices.site_amount_excl_vat'),
          value: 'siteAllocatedAmountExcludingVat',
          align: 'end',
        },
        { text: this.$t('sites.sales_invoices.site_amount'), value: 'siteAllocatedAmount', align: 'end' },
        { text: this.$t('sites.sales_invoices.total'), value: 'totalAmount', align: 'end' },
        { text: this.$t('sites.sales_invoices.status'), value: 'invoiceStatus' },
        { text: '', value: 'netvisorUrl', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    hasProject() {
      return !!(this.site && this.site.project);
    },
    totalAllocated() {
      return this.invoices.reduce((sum, inv) => sum + (Number(inv.siteAllocatedAmount) || 0), 0);
    },
    totalAllocatedExcludingVat() {
      return this.invoices.reduce((sum, inv) => sum + (Number(inv.siteAllocatedAmountExcludingVat) || 0), 0);
    },
  },
  watch: {
    'site.id'() {
      this.loadInvoices();
    },
  },
  methods: {
    async loadInvoices() {
      if (!this.site || !this.site.id || !this.hasProject) return;
      this.loading = true;
      try {
        this.invoices = await netvisorApi.getSiteSalesInvoices(this.site.id);
      } catch (error) {
        console.error('Error loading site sales invoices:', error);
        this.showError(this.$t('sites.sales_invoices.load_failed'));
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      this.syncing = true;
      try {
        await netvisorApi.syncSalesInvoices();
        await this.loadInvoices();
        this.showSuccess(this.$t('sites.sales_invoices.refreshed'));
      } catch (error) {
        console.error('Error syncing sales invoices:', error);
        this.showError(this.$t('sites.sales_invoices.refresh_failed'));
      } finally {
        this.syncing = false;
      }
    },

    lineText(line) {
      return line.description || line.productName || line.productCode || '';
    },
    // Tuotetieto sulkuihin selitteen perään; jos selitettä ei ole, tuotenimi on jo rivin tekstinä.
    lineProductInfo(line) {
      if (!line.description) return '';
      return [line.productCode, line.productName].filter(Boolean).join(' ');
    },
    // Myyntilaskurivillä on määrä ja yksikkö (ostolaskurivillä ei) - näytetään selitteen perässä.
    lineQuantity(line) {
      if (line.quantity === null || line.quantity === undefined) return '';
      const quantity = Number(line.quantity).toLocaleString('fi-FI');
      return line.unit ? `${quantity} ${line.unit}` : quantity;
    },

    formatDate(d) {
      if (!d) return '';
      if (Array.isArray(d)) {
        const [y, m, day] = d;
        return `${String(day).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
      }
      const s = String(d).substr(0, 10);
      const [y, m, day] = s.split('-');
      return day && m && y ? `${day}.${m}.${y}` : s;
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return '';
      return Number(value).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
    },

    showSuccess(message) {
      this.$store.dispatch('showNotification', { color: 'success', message, timeOut: 2000 });
    },

    showError(message) {
      this.$store.dispatch('showNotification', { color: 'error', message, timeOut: 3000 });
    },
  },
  mounted() {
    this.loadInvoices();
  },
};
</script>

<style scoped>
/* Yksi lasku = yksi <tbody>. Sisäiset reunaviivat pois, erotin vain ryhmän
   viimeiselle riville, ja koko ryhmä korostuu yhtenä hoverissa. */
.invoice-group td {
  border-bottom: none !important;
}
.invoice-group tr:last-child td {
  border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
}
/* Estetään Vuetijn rivikohtainen hover, jotta perustietorivi ei tummene
   selite-riviä enempää - sama sävy molemmille. */
.invoice-group tr:hover {
  background: transparent !important;
}
.invoice-group:hover td {
  background-color: rgba(0, 0, 0, 0.04);
}
.line-row td {
  height: auto !important;
  padding-top: 0 !important;
  padding-bottom: 8px !important;
}
/* Sisennys kertoo, että rivi kuuluu yllä olevaan laskuun. */
.line-title-cell {
  padding-left: 32px !important;
}
</style>
